import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import {
  ALL_PROCESS_CODES,
  Process,
} from 'shared/domain/process/types/model';

export type Field = StaticField | DynamicField;

export interface StaticField {
  fieldName: IssueFieldNames;
  canBeDisabled: false;
  labelId: string;
  processes: Process[];
  dependsOn?: IssueFieldNames | IssueFieldNames[];
  supportsDefaultDays?: boolean;
}
export interface DynamicField {
  fieldName: IssueFieldNames;
  canBeDisabled: true;
  labelId: string;
  processes?: undefined;
  dependsOn?: IssueFieldNames | IssueFieldNames[];
  supportsDefaultDays?: boolean;
}

// static fields means that those fields cannot be disabled/enabled in app
const staticFieldsOnAllProcesses: Field[] = [
  //cannot be disabled at all
  {
    fieldName: IssueFieldNames.issueNumber,
    canBeDisabled: false,
    labelId: 'pdf_issue_number_label' as const,
    processes: ALL_PROCESS_CODES,
  },
  {
    fieldName: IssueFieldNames.level,
    canBeDisabled: false,
    labelId: 'primaryField_Level' as const,
    processes: ALL_PROCESS_CODES,
    dependsOn: IssueFieldNames.site,
  },
  {
    fieldName: IssueFieldNames.site,
    canBeDisabled: false,
    labelId: 'primaryField_Site' as const,
    processes: ALL_PROCESS_CODES,
  },
  {
    fieldName: IssueFieldNames.stage,
    canBeDisabled: false,
    labelId: 'general_stage_label' as const,
    processes: ALL_PROCESS_CODES,
  },
  {
    fieldName: IssueFieldNames.title,
    canBeDisabled: false,
    labelId: 'primaryField_Title' as const,
    processes: ALL_PROCESS_CODES,
  },
  // can be potentially disabled
  {
    fieldName: IssueFieldNames.targetCompletionDate,
    canBeDisabled: false,
    processes: ALL_PROCESS_CODES,
    labelId: 'extendedField_Target_completion_date' as const,
    supportsDefaultDays: true,
  },
  {
    fieldName: IssueFieldNames.finalCompletionDate,
    canBeDisabled: false,
    processes: ALL_PROCESS_CODES,
    labelId: 'extendedField_Final_completion_date' as const,
  },
];

const staticFieldsOnSomeProcesses: Field[] = [
  {
    fieldName: IssueFieldNames.subcontractors,
    canBeDisabled: false,
    processes: [Process.QA, Process.ENV, Process.HS],
    labelId: 'primaryField_Subcontractors' as const,
    dependsOn: IssueFieldNames.site,
  },

  {
    fieldName: IssueFieldNames.targetStartDate,
    canBeDisabled: false,
    processes: [Process.TSK],
    labelId: 'extendedField_Target_start_date' as const,
    supportsDefaultDays: true,
  },
  {
    fieldName: IssueFieldNames.finalStartDate,
    canBeDisabled: false,
    processes: [Process.TSK],
    labelId: 'extendedField_Final_start_date' as const,
  },
  {
    fieldName: IssueFieldNames.environmentalAspect,
    canBeDisabled: false,
    processes: [Process.ENV],
    labelId: 'extendedField_Environmental_aspect_category' as const,
  },
];

const AVAILABLE_FIELDS: Field[] = [
    ...staticFieldsOnAllProcesses,
    ...staticFieldsOnSomeProcesses,
    {
      fieldName: IssueFieldNames.assignee,
      canBeDisabled: true,
      labelId: 'primaryField_Assignee' as const,
      dependsOn: IssueFieldNames.site,
    },
    {
      fieldName: IssueFieldNames.circumstances,
      canBeDisabled: true,
      labelId: 'extendedField_Circumstances' as const,
    },
    {
      fieldName: IssueFieldNames.issueVerification,
      canBeDisabled: true,
      labelId: 'extendedField_Issue_verification' as const,
    },
    {
      fieldName: IssueFieldNames.delayReason,
      canBeDisabled: true,
      labelId: 'extendedField_Delay_reason' as const,
    },
    {
      fieldName: IssueFieldNames.rejectionReason,
      canBeDisabled: true,
      labelId: 'extendedField_Rejection_reason' as const,
    },
    {
      fieldName: IssueFieldNames.completedAmount,
      canBeDisabled: true,
      labelId: 'extendedField_Completed_amount' as const,
    },
    {
      fieldName: IssueFieldNames.completionDateDelay,
      canBeDisabled: true,
      labelId: 'issue_delay_label' as const,
      dependsOn: [
        IssueFieldNames.targetCompletionDate,
        IssueFieldNames.finalCompletionDate,
      ],
    },
    {
      fieldName: IssueFieldNames.contaminatedSoilScale,
      canBeDisabled: true,
      labelId: 'extendedfield_contaminated_soil_scale' as const,
      dependsOn: IssueFieldNames.environmentalAspect,
    },
    {
      fieldName: IssueFieldNames.contractNumbers,
      canBeDisabled: true,
      labelId: 'primaryField_Contract_numbers' as const,
      dependsOn: IssueFieldNames.subcontractors,
    },
    {
      fieldName: IssueFieldNames.costCode,
      canBeDisabled: true,
      labelId: 'extendedField_Cost_code' as const,
    },
    {
      fieldName: IssueFieldNames.createdBy,
      canBeDisabled: true,
      labelId: 'issue_preview_label_created_by' as const,
    },
    {
      fieldName: IssueFieldNames.creationDate,
      canBeDisabled: true,
      labelId: 'issue_preview_label_creation_date' as const,
    },
    {
      fieldName: IssueFieldNames.decisionToImposeFine,
      canBeDisabled: true,
      labelId: 'extendedField_Decision_to_impose_a_fine' as const,
    },
    {
      fieldName: IssueFieldNames.description,
      canBeDisabled: true,
      labelId: 'primaryField_Description' as const,
    },
    {
      fieldName: IssueFieldNames.detectionDate,
      canBeDisabled: true,
      labelId: 'primaryField_Detection_date' as const,
    },
    {
      fieldName: IssueFieldNames.documents,
      canBeDisabled: true,
      labelId: 'field_documents' as const,
    },
    {
      fieldName: IssueFieldNames.linkedDocumentation,
      canBeDisabled: true,
      labelId: 'issue_section_linked_documentation' as const,
    },
    {
      fieldName: IssueFieldNames.estimatedCost,
      canBeDisabled: true,
      labelId: 'extendedField_Estimated_cost' as const,
      dependsOn: IssueFieldNames.site,
    },
    {
      fieldName: IssueFieldNames.executedByCompanies,
      canBeDisabled: true,
      labelId: 'extendedField_Executed_by_companies' as const,
      dependsOn: IssueFieldNames.site,
    },
    {
      fieldName: IssueFieldNames.executor,
      canBeDisabled: true,
      labelId: 'primaryField_Executor' as const,
      dependsOn: IssueFieldNames.site,
    },
    {
      fieldName: IssueFieldNames.expectedFine,
      canBeDisabled: true,
      labelId: 'extendedField_Expected_fine' as const,
    },

    {
      fieldName: IssueFieldNames.finalCost,
      canBeDisabled: true,
      labelId: 'extendedField_Final_cost' as const,
      dependsOn: IssueFieldNames.site,
    },

    {
      fieldName: IssueFieldNames.hashtag,
      canBeDisabled: true,
      labelId: 'issue_preview_label_hashtag' as const,
    },
    {
      fieldName: IssueFieldNames.hazardCategory,
      canBeDisabled: true,
      labelId: 'extendedField_Hazard_category' as const,
    },
    {
      fieldName: IssueFieldNames.impact,
      canBeDisabled: true,
      labelId: 'extendedField_Impact' as const,
    },
    {
      fieldName: IssueFieldNames.imposedFine,
      canBeDisabled: true,
      labelId: 'extendedField_Imposed_fine' as const,
    },

    {
      fieldName: IssueFieldNames.modificationDate,
      canBeDisabled: true,
      labelId: 'issue_tableView_header_modifiedAt_date' as const,
    },
    {
      fieldName: IssueFieldNames.modifiedBy,
      canBeDisabled: true,
      labelId: 'field_modified_by' as const,
    },
    {
      fieldName: IssueFieldNames.positionOnMap,
      canBeDisabled: true,
      labelId: 'primaryField_Position_on_map' as const,
      dependsOn: IssueFieldNames.level,
    },
    {
      fieldName: IssueFieldNames.proposedCorrectiveAction,
      canBeDisabled: true,
      labelId: 'extendedField_Proposed_corrective_action' as const,
    },
    {
      fieldName: IssueFieldNames.targetResponseToAuthorDate,
      canBeDisabled: true,
      labelId: 'extendedField_Target_response_to_author_date' as const,
      supportsDefaultDays: true,
    },
    {
      fieldName: IssueFieldNames.finalResponseToAuthorDate,
      canBeDisabled: true,
      labelId: 'extendedField_Final_response_to_author_date' as const,
    },
    {
      fieldName: IssueFieldNames.responseToAuthorDelay,
      canBeDisabled: true,
      labelId: 'issue_response_to_author_delay_label' as const,
      dependsOn: [IssueFieldNames.finalResponseToAuthorDate],
    },
    {
      fieldName: IssueFieldNames.rootCauses,
      canBeDisabled: true,
      labelId: 'extendedField_Root_cause' as const,
    },

    {
      fieldName: IssueFieldNames.soilLeakageScale,
      canBeDisabled: true,
      labelId: 'extendedfield_soil_leakage_scale' as const,
      dependsOn: IssueFieldNames.environmentalAspect,
    },
    {
      fieldName: IssueFieldNames.solutionMethod,
      canBeDisabled: true,
      labelId: 'extendedField_Final_corrective_action_description' as const,
    },
    {
      fieldName: IssueFieldNames.solutionProposal,
      canBeDisabled: true,
      labelId: 'extendedField_Proposed_corrective_action_description' as const,
    },
    {
      fieldName: IssueFieldNames.spilledSubstance,
      canBeDisabled: true,
      labelId: 'extendedfield_spilled_substance' as const,
      dependsOn: IssueFieldNames.environmentalAspect,
    },

    {
      fieldName: IssueFieldNames.startDateDelay,
      canBeDisabled: true,
      labelId: 'issue_start_date_delay_label' as const,
      dependsOn: [
        IssueFieldNames.targetStartDate,
        IssueFieldNames.finalStartDate,
      ],
    },
    {
      fieldName: IssueFieldNames.amountPercentage,
      canBeDisabled: true,
      labelId: 'issue_amount_percentage_label' as const,
      dependsOn: [
        IssueFieldNames.targetAmount,
        IssueFieldNames.completedAmount,
      ],
    },
    {
      fieldName: IssueFieldNames.subcontractorRepresentative,
      canBeDisabled: true,
      labelId: 'extendedField_Subcontractor_representative' as const,
    },

    {
      fieldName: IssueFieldNames.targetAmount,
      canBeDisabled: true,
      labelId: 'extendedField_Target_amount' as const,
    },

    {
      fieldName: IssueFieldNames.waterLeakageScale,
      canBeDisabled: true,
      labelId: 'extendedfield_water_leakage_scale' as const,
      dependsOn: IssueFieldNames.environmentalAspect,
    },
    {
      fieldName: IssueFieldNames.workTypes,
      canBeDisabled: true,
      labelId: 'extendedField_Work_type' as const,
    },
    {
      fieldName: IssueFieldNames.numberOfEmployees,
      canBeDisabled: true,
      labelId: 'extendedField_number_of_employees' as const,
    },
    {
      fieldName: IssueFieldNames.effect,
      canBeDisabled: true,
      labelId: 'extendedField_Effect' as const,
    },
    {
      fieldName: IssueFieldNames.priority,
      canBeDisabled: true,
      labelId: 'extendedField_Priority' as const,
    },
    {
      fieldName: IssueFieldNames.projectStage,
      canBeDisabled: true,
      labelId: 'extendedField_Project_stage' as const,
    },
    {
      fieldName: IssueFieldNames.personUnableToWork,
      canBeDisabled: true,
      labelId: 'extendedField_Person_unable_to_work' as const,
    },
    {
      fieldName: IssueFieldNames.daysOfInabilityToWork,
      canBeDisabled: true,
      labelId: 'extendedField_Days_of_inability_to_work' as const,
    },
    {
      fieldName: IssueFieldNames.sickLeaveStartDate,
      canBeDisabled: true,
      labelId: 'extendedField_Sick_leave_start_date' as const,
    },
    {
      fieldName: IssueFieldNames.sickLeaveEndDate,
      canBeDisabled: true,
      labelId: 'extendedField_Sick_leave_end_date' as const,
    },
    {
      fieldName: IssueFieldNames.supervisor,
      canBeDisabled: true,
      labelId: 'extendedField_Supervisor' as const,
      dependsOn: IssueFieldNames.site,
    },
    {
      fieldName: IssueFieldNames.victim,
      canBeDisabled: true,
      labelId: 'extendedField_Victim' as const,
    },
    {
      fieldName: IssueFieldNames.victimJobTitle,
      canBeDisabled: true,
      labelId: 'extendedField_Victim_job_title' as const,
    },
    {
      fieldName: IssueFieldNames.employeeNumber,
      canBeDisabled: true,
      labelId: 'extendedField_Employee_number' as const,
    },
    {
      fieldName: IssueFieldNames.employmentType,
      canBeDisabled: true,
      labelId: 'extendedField_Employment_type' as const,
    },
    {
      fieldName: IssueFieldNames.immediateMeasures,
      canBeDisabled: true,
      labelId: 'extendedField_Immediate_measures' as const,
    },
    {
      fieldName: IssueFieldNames.victimActivity,
      canBeDisabled: true,
      labelId: 'extendedField_Victim_activity' as const,
    },
    {
      fieldName: IssueFieldNames.injuryTypes,
      canBeDisabled: true,
      labelId: 'extendedField_Injury_types' as const,
    },
    {
      fieldName: IssueFieldNames.injuredBodyParts,
      canBeDisabled: true,
      labelId: 'extendedField_Injured_body_parts' as const,
    },
    {
      fieldName: IssueFieldNames.applicationNumber,
      canBeDisabled: true,
      labelId: 'extendedField_Application_number' as const,
    },
    {
      fieldName: IssueFieldNames.productType,
      canBeDisabled: true,
      labelId: 'extendedField_Product_type' as const,
    },
    {
      fieldName: IssueFieldNames.producerDistributor,
      canBeDisabled: true,
      labelId: 'extendedField_Producer_distributor' as const,
    },
    {
      fieldName: IssueFieldNames.typeModel,
      canBeDisabled: true,
      labelId: 'extendedField_Type_model' as const,
    },
    {
      fieldName: IssueFieldNames.installationLocation,
      canBeDisabled: true,
      labelId: 'extendedField_Installation_location' as const,
    },
    {
      fieldName: IssueFieldNames.requirements,
      canBeDisabled: true,
      labelId: 'extendedField_Requirements' as const,
    },
    {
      fieldName: IssueFieldNames.installationBasis,
      canBeDisabled: true,
      labelId: 'extendedField_Installation_basis' as const,
    },
    {
      fieldName: IssueFieldNames.constructionProductDocuments,
      canBeDisabled: true,
      labelId: 'extendedField_Construction_product_documents' as const,
    },
    {
      fieldName: IssueFieldNames.deviceDocuments,
      canBeDisabled: true,
      labelId: 'extendedField_Device_documents' as const,
    },
    {
      fieldName: IssueFieldNames.additionalDocuments,
      canBeDisabled: true,
      labelId: 'extendedField_Additional_documents' as const,
    },
  ];
export function getAvailableFields(): Field[] {
  return AVAILABLE_FIELDS;
}

export function getStaticFieldsOnProcess(process: Process): StaticField[] {
  return AVAILABLE_FIELDS.filter(
    (field): field is StaticField =>
      !field.canBeDisabled && field.processes.includes(process)
  );
}

const AVAILABLE_FIELD_LABEL_ID_BY_NAME = new Map(
  AVAILABLE_FIELDS.map((field) => [field.fieldName, field.labelId])
);

export function getAvailableFieldLabelId(
  fieldName: IssueFieldNames
): string {
  const labelId = AVAILABLE_FIELD_LABEL_ID_BY_NAME.get(fieldName);

  if (!labelId) {
    throw new Error(`Missing available field labelId for: ${fieldName}`);
  }

  return labelId;
}
